/**
 * Brand gradient hero atmosphere — the screenshot, enhanced.
 *
 * Renders a slowly rotating multi-color radial wash with film grain
 * overlay and vignette. Used as the hero background on Login + Dashboard.
 *
 * Layers (bottom → top):
 *   1. Cream base (var(--bg-page))
 *   2. Three large radial gradients (pink, coral, violet) at slightly
 *      offset positions — they breathe by sliding in transform
 *   3. Conic gradient at center — extra rotational color shift
 *   4. SVG film grain (~3% opacity) — analog warmth
 *   5. Radial vignette dimming the edges to focus content
 *
 * Variants:
 *   - "full"      → full-bleed (login)
 *   - "contained" → inside a rounded container (dashboard hero)
 *
 * Reduced-motion: rotation + breathe disabled, grain + vignette stay.
 */

import React from "react";

export interface BrandGradientHeroProps {
  variant?: "full" | "contained";
  /** Optional content rendered on top of the atmosphere. */
  children?: React.ReactNode;
  /** Custom min-height. Defaults: full=100vh, contained=520px */
  minHeight?: string | number;
  /** Soften the wash (lower opacity) — useful behind dense text. */
  intensity?: "soft" | "normal" | "vivid";
  className?: string;
}

const FILM_GRAIN_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
  <filter id="n">
    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" stitchTiles="stitch"/>
    <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0"/>
  </filter>
  <rect width="100%" height="100%" filter="url(#n)" opacity="0.5"/>
</svg>`)}`;

export const BrandGradientHero: React.FC<BrandGradientHeroProps> = ({
  variant = "contained",
  children,
  minHeight,
  intensity = "normal",
  className,
}) => {
  const isFull = variant === "full";
  const opacityMap = { soft: 0.45, normal: 0.75, vivid: 1.0 };
  const washOpacity = opacityMap[intensity];

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        minHeight: minHeight ?? (isFull ? "100vh" : "520px"),
        background: "var(--bg-page)",
        borderRadius: isFull ? 0 : "var(--radius-2xl)",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      {/* ── Layer 2: floating radial blobs (no rotation — drift only) ── */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "-30%",
          background:
            "radial-gradient(circle 50vw at 22% 28%, var(--brand-pink)   0%, transparent 60%)," +
            "radial-gradient(circle 55vw at 78% 32%, var(--brand-violet) 0%, transparent 60%)," +
            "radial-gradient(circle 65vw at 50% 95%, var(--brand-coral)  0%, transparent 65%)",
          opacity: washOpacity,
          filter: "blur(50px)",                    /* feather away any hard edges */
          animation: "blobDrift 26s ease-in-out infinite alternate",
          willChange: "transform",
          pointerEvents: "none",
        }}
      />

      {/* ── Layer 3: slow conic accent — circular mask + heavy blur ── */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "-60%",
          background:
            "conic-gradient(from 0deg at 50% 50%, transparent 0deg, var(--brand-pink) 70deg, transparent 140deg, var(--brand-violet) 220deg, transparent 300deg, var(--brand-coral) 350deg, transparent 360deg)",
          opacity: washOpacity * 0.4,
          mixBlendMode: "screen",
          filter: "blur(80px)",
          borderRadius: "50%",                       /* circular = no diagonal edges ever */
          animation: "conicSpin 120s linear infinite",
          willChange: "transform",
          pointerEvents: "none",
        }}
      />

      {/* ── Layer 4: film grain ────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url("${FILM_GRAIN_SVG}")`,
          backgroundSize: "200px 200px",
          opacity: 0.18,
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      />

      {/* ── Layer 5: vignette focus + cream fade at edges ─────── */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 90% 80% at center, transparent 35%, color-mix(in srgb, var(--bg-page) 50%, transparent) 80%, var(--bg-page) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* ── Content ────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          minHeight: "inherit",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default BrandGradientHero;
